import { z } from 'zod';

// Location Schema
const LocationSchema = z.object({
  address: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  stateAbbr: z.string().optional(),
  postalCode: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  ipInferredPostalCode: z.string().optional(),
});

// Enums
export const LevelOfEducationEnum = z.enum([
  'highschool',
  'ged',
  'associates',
  'bachelors',
  'masters',
  'doctorate',
]);

export const AcademicAwardsEnum = z.enum([
  'bsn',
  'bsw',
  'mba',
  'msw',
  'law-degree',
  'teaching-certificate',
  'bachelors-speech-pathology',
  'bachelors-behavioral-science',
]);

export const LicenseTypesEnum = z.enum([
  'nursing',
  'no-license',
  'in-progress',
  'licensed',
]);

export const CurrentCitizenshipEnum = z.enum(['us']);

export const WorkExperienceFieldOptionsEnum = z.enum([
  'k-12-teacher',
  'k-12-admin',
  'higher-education',
  'private-sector',
  'government-non-profit',
  'other',
]);

export const UndergraduateMajorOptionsEnum = z.enum([
  'Architecture',
  'Behavioral Science',
  'Biology',
  'Chemistry',
  'Chemical Engineering',
  'Engineering and Engineering Technology',
  'Humanities, Social Sciences and Education',
  'Management',
  'Interior Design',
  'Studio Arts',
  'Science and Technology Management',
  'Digital Media Design',
  'Business',
  'Computer Engineering',
  'Computer Science',
  'Economics',
  'Education',
  'Electrical Engineering',
  'Engineering',
  'English',
  'Government',
  'History',
  'Information Technology',
  'Mathematics',
  'Mechanical Engineering',
  'Other',
  'Other Engineering',
  'Other Sciences',
  'Non-Science',
  'Physical Science',
  'Physics',
  'Statistics',
  'Teaching',
  'Writing',
]);

export const LearningPaceOptionsEnum = z.enum(['structured', 'self-paced']);

export const CurrentPermanentResidenceCountryEnum = z.enum(['us', 'other']);

export const ProgramTrackEnum = z.enum([
  'family-nursing',
  'nurse-midwifery',
  'womens-health',
  'adult-gerontology-acute-care-nurse-practitioner',
  'applied-behavior-analysis',
  'psychology',
  'licensed-marriage-and-family-therapist',
  'licensed-professional-counselor',
  'master-of-arts-in-counseling',
  'master-of-science-in-marriage-and-family-therapy',
  'mse-in-educational-leadership',
  'mse-in-educational-leadership-with-principle-licensure',
  'preparation-principal-licensure-preparation-only',
  'master-of-arts-in-teaching',
  'master-of-arts-in-teaching-tesol',
  'MSN',
  'Post-MSN to DNP',
  'Post-BSN - FNP',
  'Post-BSN - AG-ACNP',
  'Post-BSN - MWH',
  'Post-BSN - WHNP',
  "Post-Master's",
]);

// Lead Schema
export const LeadSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  startDate: z.string().nullable().optional(),
  ipAddress: z.string().optional(),
  phoneNumber: z.string().nullable().optional(),
  location: LocationSchema.nullable().optional(),
  hsGradYear: z.number().nullable().optional(),
  levelOfEducation: LevelOfEducationEnum.nullable().optional(),
  inProgressLevelOfEducation: LevelOfEducationEnum.nullable().optional(),
  militaryStatus: z.boolean().nullable().optional(),
  gpa: z.number().nullable().optional(),
  tcpaText: z.string().nullable().optional(),
  transferCredits: z.number().nullable().optional(),
  yearsOfWorkExperience: z.number().nullable().optional(),
  yearsOfProgramRelatedExperience: z.number().nullable().optional(),
  currentAcademicAward: AcademicAwardsEnum.nullable().optional(),
  inProgressAcademicAward: AcademicAwardsEnum.nullable().optional(),
  currentLicense: LicenseTypesEnum.nullable().optional(),
  inProgressLicense: LicenseTypesEnum.nullable().optional(),
  currentCitizenship: CurrentCitizenshipEnum.nullable().optional(),
  workExperienceField: WorkExperienceFieldOptionsEnum.nullable().optional(),
  hasGivenTCPAConsent: z.boolean().nullable().optional(),
  undergraduateMajor: UndergraduateMajorOptionsEnum.nullable().optional(),
  inProgressUndergraduateMajor:
    UndergraduateMajorOptionsEnum.nullable().optional(),
  learningPace: LearningPaceOptionsEnum.nullable().optional(),
  currentPermanentResidenceCountry:
    CurrentPermanentResidenceCountryEnum.nullable().optional(),
  programTrack: ProgramTrackEnum.nullable().optional(),
  hasGivenThirdPartySharingConsent: z.boolean().nullable().optional(),
  hasGivenProspectSharingConsent: z.boolean().nullable().optional(),
  isInARelatedRole: z.boolean().optional(),
  leadSource: z.string().nullable().optional(),
  is18YearsOrOlder: z.boolean().optional(),
  someCollege: z.boolean().optional(),
  lastAttendanceYear: z.number().nullable().optional(),
  tcpaEABConsentGiven: z.boolean().nullable().optional(),
  tcpaEMCCConsentGiven: z.boolean().nullable().optional(),
  hasCompleted30SemesterHours: z.boolean().nullable().optional(),
  thirdPartyLeadId: z.string().optional(),
});

// Tracking Context Schema
export const TrackingContextSchema = z.object({
  correlationId: z.string().optional(),
  publisher: z.string().optional(),
  trafficSource: z.string().optional(),
  heclid: z.string().optional(),
  anonymousId: z.string().nullable().optional(),
  url: z.string().optional(),
  experience: z.string().optional(),
  publisherType: z.string().optional(),
});

// School Context Schema
export const SchoolContextSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
  slug: z.string().optional(),
  campus: z
    .object({
      id: z.number().optional(),
      name: z.string().optional(),
      isOnline: z.boolean().optional(),
    })
    .optional(),
});

// Program Context Schema
export const ProgramContextSchema = z.object({
  providerProgramId: z.number().nullable().optional(),
  program: z.string().optional(),
  degree: z
    .object({
      name: z.string().optional(),
      slug: z.string().optional(),
      id: z.number().optional(),
    })
    .optional(),
  category: z
    .object({
      name: z.string().optional(),
      slug: z.string().optional(),
      id: z.number().optional(),
    })
    .optional(),
  subject: z
    .object({
      name: z.string().optional(),
      slug: z.string().optional(),
      id: z.number().optional(),
    })
    .optional(),
  cap: z
    .object({
      id: z.number().optional(),
    })
    .optional(),
});

// Contact Context Schema
const ContactContextSchema = z.object({
  contactId: z.string().optional(),
});

// Message Context Schema
const MessageContextSchema = z.object({
  timestamp: z.number(),
  guid: z.string(),
  isSync: z.boolean(),
});

const MessageContextV4Schema = z.object({
  isSync: z.boolean().optional(),
  isTest: z.boolean().optional(),
});

// Qualification Request Payload Schema
export const QualificationReqPayloadSchema = z.object({
  lead: LeadSchema,
  trackingContext: TrackingContextSchema.nullable().optional(),
});

// Lead Request Schema (for submit)
export const LeadRequestSchema = z.object({
  lead: LeadSchema,
  trackingContext: TrackingContextSchema,
  schoolContext: SchoolContextSchema,
  programContext: ProgramContextSchema,
  contactContext: ContactContextSchema.optional(),
  messageContext: MessageContextSchema.optional(),
});

export const LeadSubmitV4RequestSchema = z.object({
  organizationId: z.string(),
  slug: z.string(),
  offeringUuid: z.string().optional(),
  conversionId: z.string().optional(),
  lead: LeadSchema.passthrough(),
  trackingContext: TrackingContextSchema.catchall(z.unknown()).optional(),
  messageContext: MessageContextV4Schema.optional(),
});

const IdRefSchema = z.object({ id: z.string() });

/** HXLDS closes v5 `trackingContext` to these string keys. */
const LeadSubmitV5TrackingContextSchema = z
  .object({
    correlationId: z.string().optional(),
    anonymousId: z.string().optional(),
    url: z.string().optional(),
    publisher: z.string().optional(),
    trafficSource: z.string().optional(),
    heclid: z.string().optional(),
    experience: z.string().optional(),
    publisherType: z.string().optional(),
    monetizationChannel: z.string().optional(),
  })
  .strict();

export const LEAD_SUBMIT_V5_TRACKING_KEYS =
  LeadSubmitV5TrackingContextSchema.keyof().options;

export const LeadSubmitV5RequestSchema = z.object({
  organization: IdRefSchema,
  offering: IdRefSchema.optional(),
  conversion: IdRefSchema.optional(),
  lead: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNumber: z.string().optional(),
  }),
  attributes: z.record(z.string()).optional(),
  trackingContext: LeadSubmitV5TrackingContextSchema,
  messageContext: z.object({ isSync: z.boolean(), isTest: z.boolean() }),
});

export const LeadSubmitV5ResponseSchema = z
  .object({
    submissionId: z.string().min(1),
    organizationId: z.string().optional(),
    offeringId: z.string().nullable().optional(),
  })
  .passthrough();

export const LeadSubmitV5ErrorBodySchema = z.object({
  code: z.string().optional(),
  message: z.string().optional(),
  errors: z.array(z.unknown()).optional(),
});

// Phone Validation Request Schema
export const PhoneValidationReqPayloadSchema = z.object({
  phoneNumber: z.string(),
});

// Phone Validation Response Schemas
const InvalidPhoneResponseSchema = z.object({
  validPhoneNumber: z.literal(false),
});

const ValidPhoneResponseSchema = z.object({
  phoneNumber: z.string(),
  nationalFormat: z.string(),
  validPhoneNumber: z.boolean(),
  lineTypeIntelligence: z
    .object({
      carrier_name: z.string().nullable(),
    })
    .nullable(),
});

export const ValidatePhoneResponseSchema = z.union([
  InvalidPhoneResponseSchema,
  ValidPhoneResponseSchema,
]);

// Zip Validation Request Schema
export const ZipValidationReqPayloadSchema = z.object({
  zip: z.string(),
});

// Zip Validation Response Schemas
const InvalidZipResponseSchema = z.object({
  validZip: z.literal(false),
});

const ValidZipResponseSchema = z.object({
  city: z.string(),
  zipcode: z.string(),
  state: z.string(),
  stateAbbr: z.string(),
  country: z.string().optional(),
});

export const ValidateZipResponseSchema = z.union([
  InvalidZipResponseSchema,
  ValidZipResponseSchema,
]);

// Diffy Request Schema
export const DiffyReqPayloadSchema = z.object({
  lead: LeadSchema,
});

// Property Schema
const PropertySchemaSchema = z.object({
  required: z.array(z.string()).optional(),
  properties: z.record(z.unknown()).optional(),
  additionalProperties: z.boolean().optional(),
});

// School Schema
const SchoolSchemaSchema = z.object({
  required: z.array(z.string()),
  properties: z.record(PropertySchemaSchema),
  additionalProperties: z.boolean(),
});

// Diffy Response Schema
export const DiffyResponseSchema = z.object({
  organizationId: z.string().optional(),
  offeringUuid: z.string().nullable().optional(),
  diffy: SchoolSchemaSchema,
});

// Qualification Result Schema
const QualificationResultSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  fields: z.array(z.string()),
});

// Qualification Response Schema
export const QualificationResponseSchema = z.object({
  qualified: z.array(QualificationResultSchema),
  unqualified: z.array(QualificationResultSchema),
  unknownQualifications: z.array(QualificationResultSchema),
});

// Lead Submit Response Schema
export const PartnerIdentifiersSchema = z.object({
  studentId: z.string().optional(),
  referenceId: z.string().optional(),
});

export const LeadSubmitResponseSchema = z.object({
  organizationId: z.string().optional(),
  offeringUuid: z.string().nullable().optional(),
  leadSubmitted: z.boolean(),
  PK: z.string(),
  SK: z.string(),
  referenceId: z.string().optional(),
  partnerIdentifiers: PartnerIdentifiersSchema.optional(),
});

// JSON Schema Property Schema
const JsonSchemaPropertySchema: z.ZodType<Record<string, unknown>> = z
  .object({
    type: z.string().optional(),
    enum: z.array(z.unknown()).optional(),
    default: z.unknown().optional(),
    description: z.string().optional(),
    required: z.array(z.string()).optional(),
    properties: z.record(z.lazy(() => JsonSchemaPropertySchema)).optional(),
  })
  .passthrough();

// Lead JSON Schema Response (JSON Schema format used by Ajv)
export const LeadJsonSchemaResponseSchema = z
  .object({
    $schema: z.string().optional(),
    type: z.string().optional(),
    required: z.array(z.string()).optional(),
    properties: z.record(JsonSchemaPropertySchema).optional(),
    additionalProperties: z.boolean().optional(),
  })
  .passthrough();

export const LeadJsonSchemaV4ResponseSchema = z.object({
  organizationId: z.string(),
  offeringUuid: z.string().nullable(),
  schema: LeadJsonSchemaResponseSchema,
});

// Type exports for use in TypeScript
export type Lead = z.infer<typeof LeadSchema>;
export type TrackingContext = z.infer<typeof TrackingContextSchema>;
export type SchoolContext = z.infer<typeof SchoolContextSchema>;
export type ProgramContext = z.infer<typeof ProgramContextSchema>;
export type QualificationReqPayload = z.infer<
  typeof QualificationReqPayloadSchema
>;
export type LeadRequest = z.infer<typeof LeadRequestSchema>;
export type LeadSubmitV4Request = z.infer<typeof LeadSubmitV4RequestSchema>;
export type LeadSubmitV5Request = z.infer<typeof LeadSubmitV5RequestSchema>;
export type LeadSubmitV5TrackingContext = z.infer<
  typeof LeadSubmitV5TrackingContextSchema
>;
export type LeadSubmitV5Response = z.infer<typeof LeadSubmitV5ResponseSchema>;
export type PhoneValidationReqPayload = z.infer<
  typeof PhoneValidationReqPayloadSchema
>;
export type ValidatePhoneResponse = z.infer<typeof ValidatePhoneResponseSchema>;
export type ZipValidationReqPayload = z.infer<
  typeof ZipValidationReqPayloadSchema
>;
export type ValidateZipResponse = z.infer<typeof ValidateZipResponseSchema>;
export type DiffyReqPayload = z.infer<typeof DiffyReqPayloadSchema>;
export type DiffyResponse = z.infer<typeof DiffyResponseSchema>;
export type QualificationResponse = z.infer<typeof QualificationResponseSchema>;
export type PartnerIdentifiers = z.infer<typeof PartnerIdentifiersSchema>;
export type LeadSubmitResponse = z.infer<typeof LeadSubmitResponseSchema>;
export type LeadJsonSchemaResponse = z.infer<
  typeof LeadJsonSchemaResponseSchema
>;
export type LeadJsonSchemaV4Response = z.infer<
  typeof LeadJsonSchemaV4ResponseSchema
>;
